import { db } from "../../helpers/firebase";
import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";

const COLLECTION_NAME = "userSettings";

const apiBase = () =>
  (process.env.REACT_APP_API_URL || "http://localhost:4000/api").replace(
    /\/$/,
    ""
  );

function isPermissionDenied(error) {
  return (
    error &&
    (error.code === "permission-denied" ||
      String(error.message || "").includes("Missing or insufficient permissions"))
  );
}

/** API routes only apply to the signed-in user's own doc (matches Firestore rules). */
function canUseUserSettingsApi(userId) {
  const uid = getAuth().currentUser?.uid;
  return typeof userId === "string" && uid && userId === uid;
}

async function userSettingsApiGet() {
  const user = getAuth().currentUser;
  if (!user) throw new Error("User not authenticated");
  const token = await user.getIdToken();
  const r = await fetch(`${apiBase()}/account/user-settings`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`user-settings GET ${r.status}: ${text}`);
  }
  const j = await r.json();
  return j.settings ?? null;
}

async function userSettingsApiEnsure() {
  const user = getAuth().currentUser;
  if (!user) throw new Error("User not authenticated");
  const token = await user.getIdToken();
  const r = await fetch(`${apiBase()}/account/user-settings/ensure`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`user-settings ensure ${r.status}: ${text}`);
  }
  const j = await r.json();
  return j.settings;
}

async function userSettingsApiPatch(updates) {
  const user = getAuth().currentUser;
  if (!user) throw new Error("User not authenticated");
  const token = await user.getIdToken();
  const r = await fetch(`${apiBase()}/account/user-settings`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ updates }),
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`user-settings PATCH ${r.status}: ${text}`);
  }
  const j = await r.json();
  return j.settings;
}

/**
 * Settings Model - Handles user settings (`userSettings` collection).
 * Procurement-focused product: no lease, rent, or rent-collection fields in defaults.
 * `paymentSetup` may still be written by Stripe Connect flows when present.
 *
 * Data Structure:
 * {
 *   id: string,                    // User ID (same as auth UID)
 *   userId: string,                // User ID reference
 *
 *   // Notification Preferences
 *   notifications: {
 *     repairUpdates: boolean,      // Receive repair / job updates
 *     maintenanceAlerts: boolean,  // Maintenance request alerts
 *     tenantMessages: boolean,     // Tenant message notifications
 *   },
 *
 *   // Automation Settings
 *   automation: {
 *     autoMaintenanceFollowup: boolean, // Auto-follow up on repairs / jobs
 *   },
 *
 *   // Privacy & Data Settings
 *   privacy: {
 *     allowMarketing: boolean,    // Marketing communications
 *     allowSurveys: boolean,       // Product surveys & feedback
 *     dataSharing: boolean,        // Share data for improvements
 *     analyticsTracking: boolean   // Analytics and usage tracking
 *   },
 *
 *   // Portfolio / Investor metrics (used for DSCR on dashboard when backend does not provide debtService)
 *   portfolio: {
 *     monthlyDebtService: number | null,  // Monthly debt service in dollars (e.g. mortgage P&I)
 *   },
 *
 *   // UI Preferences
 *   preferences: {
 *     theme: string,               // 'light', 'dark', 'auto'
 *     language: string,            // 'en', 'es', etc.
 *     timezone: string,            // User's timezone
 *     dateFormat: string,          // 'MM/DD/YYYY', 'DD/MM/YYYY'
 *     currency: string,            // 'USD', 'EUR', etc.
   *     emailFrequency: string,    // 'immediate', 'daily', 'weekly'
   *     dashboardImportSectionCollapsed: boolean, // Dashboard: hide bulk CSV import panel
   *     demoModePortfolio?: boolean, // false = real dashboard data even when ?debug=true; unset/true = ghost portfolio in debug
   *   },
 *
 *   // Timestamps
 *   createdAt: timestamp,
 *   updatedAt: timestamp
 * }
 */

class SettingsModel {
  /**
   * Get user settings by user ID
   */
  static async getByUserId(userId) {
    try {
      const docRef = doc(db, COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }

      return null;
    } catch (error) {
      if (isPermissionDenied(error) && canUseUserSettingsApi(userId)) {
        try {
          return await userSettingsApiGet();
        } catch (apiErr) {
          console.error("Error getting user settings (API fallback):", apiErr);
          throw apiErr;
        }
      }
      console.error("Error getting user settings:", error);
      throw error;
    }
  }

  /**
   * Create default settings for a new user
   */
  static async createDefaultSettings(userId) {
    try {
      const defaultSettings = {
        userId,
        notifications: {
          repairUpdates: true,
          maintenanceAlerts: true,
          tenantMessages: true,
        },
        automation: {
          autoMaintenanceFollowup: false,
        },
        privacy: {
          allowMarketing: false,
          allowSurveys: false,
          dataSharing: true,
          analyticsTracking: true,
        },
        portfolio: {
          monthlyDebtService: null,
        },
        /** Hybrid Compliance: default ON; users may opt out unless a mandatory-state asset forces lock (see ComplianceSettings) */
        highComplianceMode: true,
        preferences: {
          theme: "light",
          language: "en",
          timezone: "America/New_York",
          dateFormat: "MM/DD/YYYY",
          currency: "USD",
          emailFrequency: "immediate",
          /** Omit `demoModePortfolio` by default: with `?debug=true`, dashboard uses ghost data until explicitly set false. */
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = doc(db, COLLECTION_NAME, userId);
      await setDoc(docRef, defaultSettings);

      return { id: userId, ...defaultSettings };
    } catch (error) {
      if (isPermissionDenied(error) && canUseUserSettingsApi(userId)) {
        try {
          return await userSettingsApiEnsure();
        } catch (apiErr) {
          console.error(
            "Error creating default settings (API fallback):",
            apiErr
          );
          throw apiErr;
        }
      }
      console.error("Error creating default settings:", error);
      throw error;
    }
  }

  /**
   * Update specific settings
   */
  static async updateSettings(userId, updates) {
    try {
      const docRef = doc(db, COLLECTION_NAME, userId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });

      // Return updated settings
      return await this.getByUserId(userId);
    } catch (error) {
      if (isPermissionDenied(error) && canUseUserSettingsApi(userId)) {
        try {
          return await userSettingsApiPatch({ ...updates });
        } catch (apiErr) {
          console.error("Error updating settings (API fallback):", apiErr);
          throw apiErr;
        }
      }
      console.error("Error updating settings:", error);
      throw error;
    }
  }

  /**
   * Update payment setup status
   */
  static async updatePaymentSetup(userId, paymentSetupData) {
    try {
      const docRef = doc(db, COLLECTION_NAME, userId);
      await updateDoc(docRef, {
        paymentSetup: {
          ...paymentSetupData,
          lastUpdated: serverTimestamp(),
        },
        updatedAt: serverTimestamp(),
      });

      return await this.getByUserId(userId);
    } catch (error) {
      if (isPermissionDenied(error) && canUseUserSettingsApi(userId)) {
        try {
          return await userSettingsApiPatch({
            paymentSetup: {
              ...paymentSetupData,
              lastUpdated: new Date(),
            },
          });
        } catch (apiErr) {
          console.error(
            "Error updating payment setup (API fallback):",
            apiErr
          );
          throw apiErr;
        }
      }
      console.error("Error updating payment setup:", error);
      throw error;
    }
  }

  /**
   * Update notification preferences
   */
  static async updateNotifications(userId, notifications) {
    try {
      const docRef = doc(db, COLLECTION_NAME, userId);
      await updateDoc(docRef, {
        notifications,
        updatedAt: serverTimestamp(),
      });

      return await this.getByUserId(userId);
    } catch (error) {
      if (isPermissionDenied(error) && canUseUserSettingsApi(userId)) {
        try {
          return await userSettingsApiPatch({ notifications });
        } catch (apiErr) {
          console.error(
            "Error updating notifications (API fallback):",
            apiErr
          );
          throw apiErr;
        }
      }
      console.error("Error updating notifications:", error);
      throw error;
    }
  }

  /**
   * Update automation settings
   */
  static async updateAutomation(userId, automation) {
    try {
      const docRef = doc(db, COLLECTION_NAME, userId);
      await updateDoc(docRef, {
        automation,
        updatedAt: serverTimestamp(),
      });

      return await this.getByUserId(userId);
    } catch (error) {
      if (isPermissionDenied(error) && canUseUserSettingsApi(userId)) {
        try {
          return await userSettingsApiPatch({ automation });
        } catch (apiErr) {
          console.error(
            "Error updating automation settings (API fallback):",
            apiErr
          );
          throw apiErr;
        }
      }
      console.error("Error updating automation settings:", error);
      throw error;
    }
  }

  /**
   * Update privacy settings
   */
  static async updatePrivacy(userId, privacy) {
    try {
      const docRef = doc(db, COLLECTION_NAME, userId);
      await updateDoc(docRef, {
        privacy,
        updatedAt: serverTimestamp(),
      });

      return await this.getByUserId(userId);
    } catch (error) {
      if (isPermissionDenied(error) && canUseUserSettingsApi(userId)) {
        try {
          return await userSettingsApiPatch({ privacy });
        } catch (apiErr) {
          console.error(
            "Error updating privacy settings (API fallback):",
            apiErr
          );
          throw apiErr;
        }
      }
      console.error("Error updating privacy settings:", error);
      throw error;
    }
  }

  /**
   * Get or create settings for current user
   */
  static async getOrCreateSettings() {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      console.log("SettingsModel.getOrCreateSettings - User:", user);

      if (!user) {
        throw new Error("User not authenticated");
      }

      console.log(
        "SettingsModel.getOrCreateSettings - Getting settings for user:",
        user.uid
      );
      let settings = await this.getByUserId(user.uid);

      if (!settings) {
        console.log(
          "SettingsModel.getOrCreateSettings - No settings found, creating default"
        );
        settings = await this.createDefaultSettings(user.uid);
      }

      console.log(
        "SettingsModel.getOrCreateSettings - Returning settings:",
        settings
      );
      return settings;
    } catch (error) {
      console.error("SettingsModel.getOrCreateSettings - Error:", error);
      throw error;
    }
  }

  /**
   * Persist whether the dashboard "Import your portfolio" section is collapsed.
   * Uses a dotted field path so existing preference keys are not replaced wholesale.
   */
  static async setDashboardImportSectionCollapsed(userId, collapsed) {
    try {
      const docRef = doc(db, COLLECTION_NAME, userId);
      await updateDoc(docRef, {
        "preferences.dashboardImportSectionCollapsed": !!collapsed,
        updatedAt: serverTimestamp(),
      });
      return await this.getByUserId(userId);
    } catch (error) {
      if (isPermissionDenied(error) && canUseUserSettingsApi(userId)) {
        try {
          return await userSettingsApiPatch({
            "preferences.dashboardImportSectionCollapsed": !!collapsed,
          });
        } catch (apiErr) {
          console.error(
            "Error updating dashboard import section preference (API fallback):",
            apiErr
          );
          throw apiErr;
        }
      }
      console.error(
        "Error updating dashboard import section preference:",
        error
      );
      throw error;
    }
  }
}

export default SettingsModel;
